// SPDX-License-Identifier: AGPL-3.0-or-later

import {Nagbar} from '@app/features/app/components/layout/Nagbar';
import {NagbarButton} from '@app/features/app/components/layout/NagbarButton';
import {NagbarContent} from '@app/features/app/components/layout/NagbarContent';
import {NAGBAR_TONES, NagbarToneKind} from '@app/features/app/components/layout/NagbarTones';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import Updater from '@app/features/app/state/Updater';
import {getWebUpdateCountdownParts, WEB_UPDATE_FORCE_RELOAD_MS} from '@app/features/app/utils/UpdaterWebUpdate';
import {
	MINUTES_AND_SECONDS_DURATION_DESCRIPTOR,
	MINUTES_DURATION_PLURAL_DESCRIPTOR,
	SECONDS_DURATION_PLURAL_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {useNow} from '@app/features/ui/state/Tick';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

const WEB_UPDATE_MESSAGE_DESCRIPTOR = msg({
	message:
		'A new version of {productName} is available. Reload to update — this will happen automatically in {countdown}.',
	comment:
		'Non-dismissible nagbar shown when /version.json reports a newer web build than the running client. productName is the instance branding product name (Fluxer by default). countdown is a localized duration such as "2 minutes and 45 seconds". Avoid tab/browser wording; this also covers PWAs.',
});

const RELOAD_DESCRIPTOR = msg({
	message: 'Reload',
	comment: 'Button on the web-update nagbar that reloads the page to apply the new build.',
});

function formatWebUpdateCountdown(i18n: I18n, remainingMs: number): string {
	const {minutes, seconds, totalSeconds} = getWebUpdateCountdownParts(remainingMs);
	if (minutes > 0 && seconds > 0) {
		return i18n._(MINUTES_AND_SECONDS_DURATION_DESCRIPTOR, {minutes, seconds});
	}
	if (minutes > 0) {
		return i18n._(MINUTES_DURATION_PLURAL_DESCRIPTOR, {minutes});
	}
	return i18n._(SECONDS_DURATION_PLURAL_DESCRIPTOR, {seconds: totalSeconds});
}

export const WebUpdateNagbar = observer(({isMobile}: {isMobile: boolean}) => {
	const {i18n} = useLingui();
	const forceReloadAt = Updater.webForceReloadAt;
	useNow(forceReloadAt != null);
	const remainingMs = forceReloadAt == null ? WEB_UPDATE_FORCE_RELOAD_MS : Math.max(0, forceReloadAt - Date.now());
	const handleReload = useCallback(() => {
		void Updater.applyUpdate();
	}, []);
	return (
		<Nagbar
			isMobile={isMobile}
			backgroundColor={NAGBAR_TONES[NagbarToneKind.BRAND].backgroundColor}
			textColor={NAGBAR_TONES[NagbarToneKind.BRAND].textColor}
			data-flx="app.app-layout.nagbars.web-update-nagbar.nagbar"
		>
			<NagbarContent
				isMobile={isMobile}
				message={i18n._(WEB_UPDATE_MESSAGE_DESCRIPTOR, {
					productName: PRODUCT_NAME,
					countdown: formatWebUpdateCountdown(i18n, remainingMs),
				})}
				actions={
					<NagbarButton
						isMobile={isMobile}
						onClick={handleReload}
						disabled={Updater.webUpdateApplying}
						submitting={Updater.webUpdateApplying}
						data-flx="app.app-layout.nagbars.web-update-nagbar.nagbar-button.reload"
					>
						{i18n._(RELOAD_DESCRIPTOR)}
					</NagbarButton>
				}
				data-flx="app.app-layout.nagbars.web-update-nagbar.nagbar-content"
			/>
		</Nagbar>
	);
});
